/**
 * Discovery-funnel source attribution (HAL-726).
 *
 * `discovery_cta_input_focused` established this vocabulary and is the only
 * discovery event that ever carried usable page attribution. Every other
 * event in the funnel reuses these exact strings so the events join on
 * `source` in PostHog — a second, divergent vocabulary makes them unjoinable
 * and defeats the point of attributing them at all.
 *
 * Surfaces outside the article/market page templates pass their own string to
 * the `source: string` event parameters (e.g. `ResearchScanForm` sends
 * `research-*`). Only the four page templates below are constrained to this
 * union, because they render both `InlineVisibilityChecker` and
 * `StickyScrollCTA` and those two must always agree.
 */

/**
 * Source values emitted by the four article/market page templates:
 * `/learn/`, `/compare/`, `/problems/`, `/for/`.
 */
export const DISCOVERY_PAGE_SOURCES = ['learn', 'compare', 'problem', 'market'] as const;

export type DiscoveryPageSource = (typeof DISCOVERY_PAGE_SOURCES)[number];

/**
 * Query-string key used to carry the originating source from the page that
 * started a check to the resulting `/discovery-report/{checkId}` page. The
 * check row itself has no `source` column, so the link is the only channel
 * that survives the new-tab hand-off.
 */
export const DISCOVERY_SOURCE_PARAM = 'source';

/**
 * Narrow an untrusted value (a query-string param) to a known source.
 *
 * Allowlist-only on purpose: the parsed value lands in a PostHog property, and
 * echoing arbitrary user-supplied strings there would both pollute the
 * attribution dimension and let a crafted link write junk into analytics.
 * Anything unrecognised resolves to `undefined`, which callers treat as "no
 * attribution" rather than substituting a placeholder.
 */
export function parseDiscoveryPageSource(
  raw: string | string[] | undefined | null,
): DiscoveryPageSource | undefined {
  const candidate = Array.isArray(raw) ? raw[0] : raw;
  if (typeof candidate !== 'string') return undefined;
  return (DISCOVERY_PAGE_SOURCES as readonly string[]).includes(candidate)
    ? (candidate as DiscoveryPageSource)
    : undefined;
}

/**
 * Append the originating source to a discovery-report URL.
 *
 * The create-check API returns a bare relative path today, but the separator
 * is chosen from the URL rather than assumed so a future query string on that
 * response does not silently produce a malformed link.
 */
export function withDiscoverySource(url: string, source: DiscoveryPageSource): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${DISCOVERY_SOURCE_PARAM}=${encodeURIComponent(source)}`;
}
